import { useCallback, useMemo } from 'react';

import { Popover, PopoverProps } from '@mui/material';
import { useForm } from 'react-final-form';

import { Icons, Tooltip, Skeleton, Stack } from '@cast/design-system';

import { useGetWebhookCategoriesQuery } from 'hooks/queries/notifications';

import { DEFAULT_TEMPLATES } from './constants';
import { FormModel } from './types';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { RffSelect } from '@cast/shared-rff-ui';

export const CategorySelect = () => {
  const { categories, isLoading, error, refetch } =
    useGetWebhookCategoriesQuery();
  const form = useForm<FormModel>();

  const options = useMemo(() => {
    if (error) {
      return [];
    }

    return [
      ...(categories?.map((category) => ({
        title: category.name,
        value: category.value,
      })) || []),
      {
        title: 'Other',
        value: undefined,
      },
    ];
  }, [categories, error]);

  const handleChange = useCallback(
    (_: object, newCategory: string) => {
      const templateState = form.getFieldState('requestTemplate');
      if (templateState?.pristine) {
        form.reset({
          ...form.getState().values,
          requestTemplate: DEFAULT_TEMPLATES[newCategory] ?? '',
        });
      }
    },
    [form]
  );

  if (isLoading) {
    return <Skeleton width="100%" height={54} />;
  }

  return (
    <RffSelect
      testId="alert-category-select"
      label={
        <Stack direction="row" alignItems="center" gap={4}>
          Category
          <Tooltip
            title="Customize notifications by choosing specific categories and receive alerts that matter the most."
            arrow
            placement="top"
          >
            <Icons.Info />
          </Tooltip>
        </Stack>
      }
      name="category"
      options={options}
      optionLabel="title"
      optionValue="value"
      renderValue="title"
      PopoverComponent={
        error
          ? (props: PopoverProps) => (
              <Popover {...props}>
                <FailedToLoad
                  title="Failed to load categories"
                  sx={{
                    width: (props.anchorEl as HTMLElement)?.offsetWidth,
                    minWidth: 200,
                    padding: 16,
                  }}
                  refresh={refetch}
                />
              </Popover>
            )
          : undefined
      }
      onChange={handleChange}
      applySortingWhileSearching={false}
    />
  );
};
